/* @jsx jsx */
import { jsx } from '@balance-web/core';
import type { CustomTriggerProps } from '@balance-web/dropdown-v2';
import { ChevronDownIcon, ChevronUpIcon } from '@balance-web/icon';
import { useTheme } from '@balance-web/theme';
import { ternary, useDefaultInteractiveStyles } from '@balance-web/utils';
import { Text } from '@balance-web/text';
import { Flex } from '@balance-web/flex';

export type CreateDropdownNavigationTriggerProps = {
  label: string;
  isSelected: boolean;
  visible?: boolean;
};
export const createDropdownNavigationTrigger = ({
  label,
  isSelected,
  visible = true,
}: CreateDropdownNavigationTriggerProps) => {
  return ({ isOpen, triggerProps }: CustomTriggerProps) => {
    const { semantic, typography, spacing } = useTheme();
    const defaultInteractiveStyles = useDefaultInteractiveStyles();
    const CheveronIcon = isOpen ? ChevronUpIcon : ChevronDownIcon;

    return (
      <Flex
        alignItems="center"
        alignSelf="center"
        css={{
          position: 'relative',
          lineHeight: typography.leading.base,
          ':after': ternary(
            isSelected,
            {
              background: semantic.colour.secondary.accent,
              borderRadius: 4,
              bottom: 0,
              content: '" "',
              height: 4,
              right: spacing.small,
              left: spacing.small,
              position: 'absolute' as const,
            },
            undefined
          ),
        }}
        height="100%"
        id={'menu-id-' + label}
        {...triggerProps}
      >
        <Flex
          alignItems="center"
          as="button"
          borderRadius="medium"
          css={{
            ...defaultInteractiveStyles,
            ...ternary(
              isOpen,
              {
                background: semantic.colour.surface.neutral.background4,
              },
              undefined
            ),
            ...ternary(!visible, { pointerEvents: 'none' as const }, undefined),
          }}
          gap="small"
          paddingX="small"
          paddingY="medium"
          tabIndex={visible ? undefined : -1}
        >
          <Text
            color={isSelected ? 'textPrimary' : 'textBody'}
            weight="semibold"
          >
            {label}
          </Text>
          <Flex alignSelf="center">
            <CheveronIcon
              color={isSelected ? 'textPrimary' : 'textBody'}
              size="small"
            />
          </Flex>
        </Flex>
      </Flex>
    );
  };
};
